import { useEffect, useRef, useState } from 'react';
import { config } from '../../../config/config';

type UseMockVolumeOptions = {
    min?: number;
    max?: number;
    intervalMs?: number;
    maxChangePercent?: number;
};

let cachedVolume: Record<string, number> = {};
const listeners = new Set<() => void>();
let intervalId: ReturnType<typeof setInterval> | null = null;

const startValues: Record<string, number> = {
    BTCUSDC: 50000,
    ETHUSDC: 35000,
    LINKUSDC: 12000,
    BNBUSDC: 18000,
    XRPUSDC: 9000,
    ADAUSDC: 7000,
    MATICUSDC: 8000,
    DOTUSDC: 10000,
    ARBUSDC: 15000,
    DOGEUSDC: 20000,
    UNIUSDC: 11000,
};

const initCache = (min: number, max: number) => {
    if (Object.keys(cachedVolume).length > 0) return;
    config.chartSymbols.forEach((chartSymbol) => {
        cachedVolume[chartSymbol] = startValues[chartSymbol] ?? (min + max) / 2;
    });
};

const startInterval = (min: number, max: number, intervalMs: number, maxChangePercent: number) => {
    if (intervalId) return;
    intervalId = setInterval(() => {
        const updated: Record<string, number> = {};
        Object.entries(cachedVolume).forEach(([symbol, vol]) => {
            const maxChange = vol * maxChangePercent;
            const change = (Math.random() * 2 - 1) * maxChange;
            updated[symbol] = Math.max(min, Math.min(max, vol + change));
        });
        cachedVolume = updated;
        listeners.forEach((fn) => fn());
    }, intervalMs);
};

const stopInterval = () => {
    if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
    }
};

const useMockVolume = ({
    min = 10,
    max = 75000,
    intervalMs = 1000,
    maxChangePercent = 0.02,
}: UseMockVolumeOptions = {}) => {
    const [volume, setVolume] = useState<Record<string, number>>(() => {
        initCache(min, max);
        return { ...cachedVolume };
    });

    const volumeRef = useRef(volume);

    useEffect(() => {
        startInterval(min, max, intervalMs, maxChangePercent);

        const listener = () => {
            volumeRef.current = { ...cachedVolume };
            setVolume({ ...cachedVolume });
        };
        listeners.add(listener);

        return () => {
            listeners.delete(listener);
            if (listeners.size === 0) {
                stopInterval();
            }
        };
    }, [min, max, intervalMs, maxChangePercent]);

    return { volume };
};

export default useMockVolume;
